import { RefNodeSlotsProvider } from '@blocksuite/affine-inline-reference';
import type { DatabaseBlockModel } from '@blocksuite/affine-model';
import {
  BaseCellRenderer,
  createFromBaseCellRenderer,
  createIcon,
} from '@blocksuite/data-view';
import { computed, effect } from '@preact/signals-core';
import { html } from 'lit/static-html.js';

import { EditorHostKey } from '../../context/host-context.js';
import {
  relationAddButtonStyle,
  relationChipMissingStyle,
  relationChipStyle,
  relationContainerStyle,
} from './cell-renderer-css.js';
import { relationPropertyModelConfig } from './define.js';
import { removePairColumn, syncBackRelation } from './pairing.js';
import { popRowPicker } from './row-picker.js';
import { buildRelationConfigMenu } from './target-picker.js';
import type { RelationPropertyData, RelationRawValue } from './types.js';

interface ResolvedRef {
  docId: string;
  blockId: string;
  title: string;
  missing: boolean;
}

export class RelationCell extends BaseCellRenderer<
  RelationRawValue,
  RelationRawValue,
  RelationPropertyData
> {
  // Tracks docIds we've ever attempted to load — never cleared within this
  // component's lifetime. Prevents infinite re-load loops when doc.load()
  // doesn't transition doc.loaded → true (e.g. unsynced sub-doc).
  private readonly _attemptedLoad = new Set<string>();
  private _loadingFlag = false;

  private get _std() {
    return this.view.serviceGet(EditorHostKey)?.std;
  }

  private _scheduleLoad(docId: string) {
    if (this._attemptedLoad.has(docId)) return;
    this._attemptedLoad.add(docId);
    const std = this._std;
    if (!std) return;
    const doc = std.workspace.getDoc(docId);
    if (!doc || doc.loaded) return;
    this._loadingFlag = true;
    try {
      doc.load(() => {
        this._loadingFlag = false;
        this.requestUpdate();
      });
    } catch (err) {
      this._loadingFlag = false;
      console.warn('[orghub-relation] doc.load failed', docId, err);
    }
  }

  private readonly _targetDb$ = computed<DatabaseBlockModel | null>(() => {
    const data = this.property.data$.value;
    if (!data.targetBlockId) return null;
    const std = this._std;
    if (!std) return null;
    const targetDocId = data.targetDocId ?? std.store.id;
    const doc = std.workspace.getDoc(targetDocId);
    if (!doc) return null;
    if (!doc.loaded) {
      // Only queue a load attempt the first time we encounter this docId.
      if (!this._attemptedLoad.has(targetDocId)) {
        queueMicrotask(() => this._scheduleLoad(targetDocId));
      }
      return null;
    }
    const block = doc
      .getStore({ id: doc.id })
      .getBlock(data.targetBlockId)?.model;
    if (!block || block.flavour !== 'affine:database') return null;
    return block as unknown as DatabaseBlockModel;
  });

  private readonly _resolvedRefs$ = computed<ResolvedRef[]>(() => {
    const refs = this.value ?? [];
    const db = this._targetDb$.value;
    return refs.map(ref => {
      if (!db) {
        return {
          docId: ref.docId,
          blockId: ref.blockId,
          title: ref.blockId.slice(0, 6),
          missing: false,
        };
      }
      const row = db.store.getBlock(ref.blockId)?.model;
      if (!row) {
        return {
          docId: ref.docId,
          blockId: ref.blockId,
          title: 'Row deleted',
          missing: true,
        };
      }
      const text = row.text?.toString().trim();
      return {
        docId: ref.docId,
        blockId: ref.blockId,
        title: text && text.length > 0 ? text : 'Untitled',
        missing: false,
      };
    });
  });

  private _pickerHandle?: { close: () => void };

  private readonly _writeRefs = (next: RelationRawValue) => {
    const prev = this.value ?? [];
    const data = this.property.data$.value;
    const std = this._std;
    // Single-ref mode: clamp to at most one ref.
    const clamped =
      data.singleRef && next.length > 1 ? [next[next.length - 1]!] : next;
    this.valueSetImmediate(clamped);
    if (std && data.pairedColumnId) {
      syncBackRelation({
        std,
        sourceDocId: std.store.id,
        sourceRowId: this.row.rowId,
        pairedColumnId: data.pairedColumnId,
        prevRefs: prev,
        nextRefs: clamped,
      });
    }
  };

  private readonly _openPicker = () => {
    if (this._pickerHandle) return;
    const db = this._targetDb$.value;
    if (!db) return;
    const data = this.property.data$.value;
    const std = this._std;
    if (!std) return;
    const targetDocId = data.targetDocId ?? std.store.id;
    this._pickerHandle = popRowPicker({
      anchor: this,
      targetDb: db,
      targetDocId,
      currentValue: this.value ?? [],
      singleRef: data.singleRef ?? false,
      onChange: this._writeRefs,
      onComplete: () => {
        this._pickerHandle = undefined;
        this.selectCurrentCell(false);
      },
    });
  };

  private _deletionUnsubscribe?: () => void;

  override connectedCallback() {
    super.connectedCallback();
    // Watch target DB for row deletions and prune dead refs.
    const stop = effect(() => {
      this._deletionUnsubscribe?.();
      this._deletionUnsubscribe = undefined;
      const db = this._targetDb$.value;
      if (!db) return;
      const sub = db.store.slots.blockUpdated.subscribe(payload => {
        if (payload.type !== 'delete') return;
        const refs = this.value ?? [];
        if (refs.length === 0) return;
        if (!refs.some(r => r.blockId === payload.id)) return;
        const next = refs.filter(r => r.blockId !== payload.id);
        // Use the editing-safe write path so pair sync still runs.
        this._writeRefs(next);
      });
      this._deletionUnsubscribe = () => sub.unsubscribe();
    });
    this._disposables.add({
      dispose: () => {
        this._deletionUnsubscribe?.();
        this._deletionUnsubscribe = undefined;
        stop();
      },
    });
  }

  override afterEnterEditingMode() {
    this._openPicker();
  }

  override beforeExitEditingMode() {
    // Intentionally does NOT close the picker: writing a value re-renders
    // the table, which can flip this cell out of editing mode mid-pick.
    // Closing here used to dismiss the picker after every single row
    // selection, making multi-row picks impossible. The picker closes
    // itself on outside click / Escape (its `onComplete`) and when the
    // cell is disconnected.
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this._pickerHandle?.close();
    this._pickerHandle = undefined;
  }

  private readonly _onChipClick = (e: MouseEvent, ref: ResolvedRef) => {
    if (ref.missing) return;
    e.stopPropagation();
    const std = this._std;
    if (!std) return;
    const data = this.property.data$.value;
    std.getOptional(RefNodeSlotsProvider)?.docLinkClicked.next({
      pageId: ref.docId,
      params: {
        databaseId: data.targetBlockId ?? undefined,
        databaseRowId: ref.blockId,
      },
      event: e,
      host: std.host,
    });
  };

  override render() {
    const resolved = this._resolvedRefs$.value;
    const editing = this.isEditing$.value;
    const data = this.property.data$.value;
    const hasTarget = !!data.targetBlockId;
    const targetDb = this._targetDb$.value;
    const isLoading = hasTarget && targetDb == null && this._loadingFlag;
    const maxVisible = data.maxVisibleRefs ?? 5;
    const visibleRefs = editing ? resolved : resolved.slice(0, maxVisible);
    const overflow = editing ? 0 : Math.max(0, resolved.length - maxVisible);
    return html`
      <div class="${relationContainerStyle}" data-testid="relation-cell">
        ${isLoading && resolved.length === 0
          ? html`<span
              class="${relationChipStyle}"
              style="opacity:0.6;font-style:italic;"
              >Loading…</span
            >`
          : ''}
        ${visibleRefs.map(
          ref =>
            html`<span
              class="${relationChipStyle} ${ref.missing
                ? relationChipMissingStyle
                : ''}"
              data-testid="relation-ref-chip"
              title="${ref.title}"
              style="${ref.missing ? '' : 'cursor:pointer;'}"
              @click="${(e: MouseEvent) => this._onChipClick(e, ref)}"
              >${ref.title}</span
            >`
        )}
        ${overflow > 0
          ? html`<span
              class="${relationChipStyle}"
              style="opacity:0.7;font-style:italic;"
              title="${`+${overflow} more`}"
              >+${overflow} more</span
            >`
          : ''}
        ${editing && hasTarget
          ? html`<span class="${relationAddButtonStyle}">+</span>`
          : ''}
      </div>
    `;
  }
}

export const relationColumnConfig =
  relationPropertyModelConfig.createPropertyMeta({
    icon: createIcon('DualLinkIcon'),
    cellRenderer: {
      view: createFromBaseCellRenderer(RelationCell),
    },
    propertyConfigMenu: buildRelationConfigMenu,
    onBeforeColumnDelete: ({ property }) => {
      const data = property.data$.value as RelationPropertyData;
      if (!data.pairedColumnId || !data.targetDocId || !data.targetBlockId) {
        return;
      }
      const std = property.view.serviceGet(EditorHostKey)?.std;
      if (!std) return;
      const targetDoc = std.workspace.getDoc(data.targetDocId);
      if (!targetDoc || !targetDoc.loaded) return;
      const targetDb = targetDoc
        .getStore({ id: targetDoc.id })
        .getBlock(data.targetBlockId)?.model as DatabaseBlockModel | undefined;
      if (!targetDb) return;
      removePairColumn(targetDb, data.pairedColumnId);
    },
  });
